/**
 * Create from files, last step: the editor prefilled with the proposal (fields, sections, success cases, files as
 * materials), the gaps beside it. **Create draft** sends everything in one `POST /api/assets/from-extraction` and
 * opens the new asset; **Cancel** drops the draft (nothing was saved). A file the server no longer holds is a 409
 * naming it: leave it out or start again with the file uploaded anew.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateFromExtraction } from '@/api/queries';
import type { ProposalOut } from '@/api/types/extractions';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { FormError } from '@/ui/StateCard';

import { AssetFieldsForm } from '../fields/AssetFieldsForm';
import { draftProblems, saveBody, type TemplateSection } from './fromFilesDraft';
import { ReviewCases } from './ReviewCases';
import { ReviewMaterials } from './ReviewMaterials';
import { fileNames } from '../files/SourceNote';
import { ReviewNotices } from './ReviewNotices';
import { ReviewSections } from './ReviewSections';
import { useFromFilesDraft } from './useFromFilesDraft';

export function ReviewStep({ proposal, template, onStartOver }: { proposal: ProposalOut; template: TemplateSection[]; onStartOver: () => void }) {
  const navigate = useNavigate();
  const create = useCreateFromExtraction();
  const { draft, update, forget } = useFromFilesDraft(proposal, template);
  const [cancelling, setCancelling] = useState(false);
  const names = fileNames(proposal.files);
  const problems = draftProblems(draft);

  function save() {
    if (problems.length > 0) return;
    create.mutate(saveBody(draft), {
      onSuccess: (asset) => {
        forget();
        navigate(`/assets/${asset.id}`);
      },
    });
  }

  return (
    <div className="stack-md">
      <ReviewNotices proposal={proposal} draft={draft} />
      <section className="card asset-form" aria-labelledby="review-fields-title">
        <div className="card-body stack-md">
          <h2 id="review-fields-title">Asset</h2>
          <AssetFieldsForm draft={draft.fields} set={(patch) => update((d) => ({ ...d, fields: { ...d.fields, ...patch } }))} />
        </div>
      </section>
      <ReviewSections
        sections={draft.sections}
        names={names}
        onChange={(key, patch) => update((d) => ({ ...d, sections: d.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)) }))}
      />
      <ReviewCases
        cases={draft.cases}
        names={names}
        onChange={(key, form) => update((d) => ({ ...d, cases: d.cases.map((c) => (c.key === key ? { ...c, form } : c)) }))}
        onRemove={(key) => update((d) => ({ ...d, cases: d.cases.filter((c) => c.key !== key) }))}
      />
      <ReviewMaterials
        materials={draft.materials}
        onChange={(fileId, patch) => update((d) => ({ ...d, materials: d.materials.map((m) => (m.file_id === fileId ? { ...m, ...patch } : m)) }))}
      />
      <div className="card">
        <div className="card-body stack-md">
          <FormError error={create.error} />
          {problems.length > 0 && (
            <ul className="hint" aria-live="polite" aria-label="Before you can save">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setCancelling(true)}>Cancel</button>
            <ActionButton type="button" state={buttonState(create)} label="Create draft" pendingLabel="Creating…" doneLabel="Created" disabled={problems.length > 0} onClick={save} />
          </div>
        </div>
      </div>
      {cancelling && (
        <ConfirmDialog
          title="Discard this draft?"
          confirmLabel="Discard draft"
          danger
          onClose={() => setCancelling(false)}
          onConfirm={() => {
            forget();
            onStartOver();
            navigate('/assets');
          }}
        >
          <p>Nothing was saved to the catalog. Uploaded files nobody uses are removed after a day.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
